import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { useAuth, homeFor } from "../../lib/auth";
import { useSeo } from "../../lib/site";
import { Logo } from "../../components/site/Layout";
import { Field, inputCls, Spinner } from "../../components/common";

export function googleLogin(next = "/student") {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  sessionStorage.setItem("hr_after_google", next);
  const redirectUrl = window.location.origin + "/auth/callback";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export function AuthForm({ initialMode = "login", onDone, compact }) {
  const [mode, setMode] = useState(initialMode);
  const [d, setD] = useState({ name: "", email: "", phone: "", password: "" });
  const [busy, setBusy] = useState(false);
  const { setUser } = useAuth();
  const set = (k) => (e) => setD({ ...d, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "forgot") {
        const { data } = await api.post("/auth/forgot-password", { email: d.email });
        toast.success(data.message);
        setMode("login");
      } else {
        const { data } = await api.post(mode === "login" ? "/auth/login" : "/auth/register", mode === "login" ? { email: d.email, password: d.password } : d);
        setUser(data);
        onDone?.(data);
      }
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
      <h2 className="font-heading text-2xl font-extrabold">{mode === "login" ? "Welcome back" : mode === "register" ? "Create your account" : "Reset your password"}</h2>
      {mode === "register" && <Field label="Full name" required><input required className={inputCls} value={d.name} onChange={set("name")} data-testid="register-name-input" autoComplete="name" /></Field>}
      <Field label="Email" required><input required type="email" className={inputCls} value={d.email} onChange={set("email")} data-testid="login-email-input" autoComplete="email" /></Field>
      {mode === "register" && <Field label="Phone"><input type="tel" className={inputCls} value={d.phone} onChange={set("phone")} data-testid="register-phone-input" autoComplete="tel" /></Field>}
      {mode !== "forgot" && <Field label="Password" required hint={mode === "register" ? "At least 8 characters" : undefined}><input required type="password" minLength={mode === "register" ? 8 : 1} className={inputCls} value={d.password} onChange={set("password")} data-testid="login-password-input" autoComplete={mode === "login" ? "current-password" : "new-password"} /></Field>}
      <button disabled={busy} className="btn-primary w-full" data-testid="login-submit-button">{busy ? "Please wait…" : mode === "login" ? "Sign in" : mode === "register" ? "Create account" : "Send reset link"}</button>
      {mode !== "forgot" && (
        <button type="button" onClick={() => googleLogin(compact ? window.location.pathname + window.location.search : undefined)} className="btn-outline w-full" data-testid="google-login-btn">
          <svg className="h-4 w-4" viewBox="0 0 48 48" aria-hidden><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" /><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" /><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" /><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 38.2 44 33 44 24c0-1.3-.1-2.4-.4-3.5z" /></svg>
          Continue with Google
        </button>
      )}
      <div className="flex flex-wrap justify-between gap-2 text-sm">
        {mode === "login" ? <button type="button" className="font-semibold text-red-600" onClick={() => setMode("register")} data-testid="login-register-link">Create an account</button>
          : <button type="button" className="font-semibold text-red-600" onClick={() => setMode("login")} data-testid="auth-login-link">I already have an account</button>}
        {mode === "login" && <button type="button" className="text-slate-500" onClick={() => setMode("forgot")} data-testid="login-forgot-password-link">Forgot password?</button>}
      </div>
    </form>
  );
}

export default function Login() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const portal = sp.get("portal");
  useSeo({ title: "Login", noindex: true });
  useEffect(() => { if (user) nav(sp.get("next") || homeFor(user), { replace: true }); }, [user]); // eslint-disable-line
  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="relative hidden bg-slate-950 lg:block">
        <img src="https://images.unsplash.com/photo-1631217868264-e5b90bb7e133?crop=entropy&cs=srgb&fm=jpg&q=70&w=1200" alt="" className="absolute inset-0 h-full w-full object-cover opacity-40" />
        <div className="relative flex h-full flex-col justify-end p-12 text-white"><h1 className="font-heading text-4xl font-extrabold">One portal for your <span className="text-red-500">healthcare career</span>.</h1><p className="mt-3 text-slate-300">Applications, documents, course materials, schedules and payments — all in one place.</p></div>
      </div>
      <div className="flex flex-col justify-center px-6 py-12 sm:px-16">
        <Link to="/" className="mb-10" data-testid="login-home-link"><Logo className="h-14" /></Link>
        <div className="w-full max-w-md">
          {portal && <p className="eyebrow mb-3">{portal} portal</p>}
          <AuthForm initialMode={sp.get("mode") === "register" ? "register" : "login"} />
        </div>
      </div>
    </div>
  );
}

export function AuthCallback() {
  const loc = useLocation();
  const nav = useNavigate();
  const { setUser } = useAuth();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const sid = new URLSearchParams(loc.hash.slice(1)).get("session_id");
    api.post("/auth/google", { session_id: sid }).then(({ data }) => {
      setUser(data);
      const next = sessionStorage.getItem("hr_after_google");
      sessionStorage.removeItem("hr_after_google");
      nav(next || homeFor(data), { replace: true });
    }).catch((e) => { toast.error(errMsg(e)); nav("/login", { replace: true }); });
  }, []); // eslint-disable-line
  return <Spinner label="Signing you in" />;
}

export function ResetPassword() {
  const [sp] = useSearchParams();
  const [pw, setPw] = useState("");
  const nav = useNavigate();
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/auth/reset-password", { token: sp.get("token"), password: pw }); toast.success("Password updated. Please sign in."); nav("/login"); }
    catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div className="container-x max-w-md py-20">
      <form onSubmit={submit} className="card space-y-4 p-8" data-testid="reset-form">
        <h1 className="font-heading text-2xl font-extrabold">Choose a new password</h1>
        <Field label="New password" hint="At least 8 characters"><input type="password" minLength={8} required className={inputCls} value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-password-input" /></Field>
        <button className="btn-primary w-full" data-testid="reset-submit">Update password</button>
      </form>
    </div>
  );
}

export function VerifyEmail() {
  const [sp] = useSearchParams();
  const [msg, setMsg] = useState("Verifying…");
  useEffect(() => { api.get(`/auth/verify?token=${sp.get("token")}`).then(() => setMsg("Your email is verified. Thank you!")).catch((e) => setMsg(errMsg(e))); }, []); // eslint-disable-line
  return <div className="container-x py-24 text-center"><p className="font-heading text-xl font-bold" data-testid="verify-message">{msg}</p><Link to="/login" className="btn-primary mt-6">Go to login</Link></div>;
}
